<template>
  <div>
    <SystemUserFilter
      v-if="showSystemUserFilter"
      data-test-id="ItemTableActionBar__systemUserFilter"
    />
    <ItemTableActionBarMessageList
      :inDetailsAsidePanel="inDetailsAsidePanel"
      :itemType="itemType"
      :setLoadingOverlay="setLoadingOverlay"
    />
    <div :class="$style.actionBarMain">
      <div>
        <button
          v-if="showMainAction"
          :class="['btnMainAction', $style.mainActionButton, {
            [$style.mainActionButtonActive]: mainActionMenuVisible,
            [$style.mainActionButtonDisabled]: isMainActionDisabled,
          }]"
          data-walkthrough="item-table-main-action-button"
          :disabled="isMainActionDisabled"
          :title="mainActionTitle"
          @click="handleMainActionButtonClick"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" :class="$style.icon" v-bind:data-test-id="'ItemTableActionBar__icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" d="M9.062.991a.991.991 0 10-1.983 0v5.947H.991a.991.991 0 000 1.983H7.08v6.088a.991.991 0 001.983 0V8.92h5.947a.991.991 0 000-1.983H9.062V.991z"/></svg>
        </button>
        <DropDownList
          v-if="hasMainActionMenu"
          :class="$style.mainActionMenu"
          :closeDropDown="hideMainActionMenu"
          :isLargeDropDown="false"
          :itemType="itemType"
          itemSection="Item Table Action Bar"
          :menuSections="mainActionMenuSections"
          :onItemClick="hideMainActionMenu"
          :showList="mainActionMenuVisible"
          :showMenuLabel="!!mainActionMenuSections[0].menuLabel"
        />
      </div>
      <div :class="$style.actionsContainer">
        <div :class="$style.actionsContent">
          <ItemTableSearchBar
            v-if="showSearchForm"
            :class="$style.searchBar"
            :disableSearchBar="disableSearchBar"
            :placeholderText="placeholderText"
            :preventSearchReset="preventSearchReset"
            :selectionItemText="selectionItemText"
            :setLoadingOverlay="setLoadingOverlay"
            :showSearchForm="showSearchForm"
            :storeModule="storeModule"
            :useDropDownFilterComponent="useDropDownFilterComponent"
            :useModernSearchStyles="useModernSearchStyles"
            :useSearchContextFilterComponent="useSearchContextFilterComponent"
          />
          <div :class="$style.nonSearchActionsContainer">
            <slot
              name="customBar"
            />
            <div :class="[$style.selectionText, selectionTextClass]">
              <div
                v-if="showSelectedItemsCount"
                :class="$style.selectionResults"
              >
                <MarkdownInline
                  data-test-id="selectionResultsText"
                  :mdTemplate="selectionResultsText"
                />
              </div>
              <label
                v-if="disableBoundCount ? false : showBound"
                :class="$style.selectionBoundItems"
                :title="boundItemsLabelTitle"
              >
                <input
                  :checked="areShowingBoundItems"
                  :class="$style.selectionBoundItemsInput"
                  :disabled="!isBoundItemsInputEnabled"
                  type="checkbox"
                  @change="handleBoundItemsInputClick($event.target.checked)"
                >
                {{ boundItemsLabelText }}
              </label>
            </div>
            <div
              v-show="!isAsideOpen"
              :class="[$style.bulkActions, bulkActionsPositioningClass]"
            >
              <button
                v-if="showRefreshButton"
                :class="['btnCTA', 'btnDefault', $style.bulkButton]"
                data-test-id="refreshButton"
                :disabled="!isRefreshButtonEnabled"
                @click="handleRefresh"
              >
                refresh list
              </button>
              <button
                v-if="showBulkAction"
                :class="['btnCTA', 'btnDefault', $style.bulkButton]"
                data-test-id="bulkActionButton"
                :disabled="!isBulkEnabled || isBulkMainActionDisabled"
                @click="handleBulkAction"
              >
                {{ $props.bulkActionText }}
              </button>
              <DropDownMenu
                v-if="bulkActionsList.length"
                :class="$style.bulkButton"
                :disabled="!isBulkEnabled"
                :isLargeDropDown="false"
                :menuSections="bulkActionsList"
                menuTitle="change state"
                :storeModule="storeModule"
                :useButtonToActivate="true"
              />
              <DropDownMenu
                v-if="additionalBulkActions.length"
                :class="$style.bulkButton"
                :disabled="!isBulkEnabled"
                :isLargeDropDown="false"
                :menuSections="additionalBulkActions"
                menuTitle="more actions"
                :storeModule="storeModule"
                :useButtonToActivate="true"
              />
              <button
                v-if="showBulkDelete"
                :class="['btnAlert', 'btnDefault', $style.bulkButton]"
                data-test-id="bulkDeleteButton"
                :disabled="!isBulkEnabled"
                @click="handleBulkDelete"
              >
                {{ $props.bulkDeleteText }}
              </button>
              <IconButton
                v-if="showManagePolicyGroupTemplates"
                :class="[$style.manageTemplatesButton]"
                data-test-id="manageTemplatesButton"
                :disabled="false"
                @click="handleManagePolicyGroupTemplatesClick"
              >
                <SettingsIcon :class="$style.manageTemplateIcon" />
                Manage Policy Group Templates
              </IconButton>
            </div>
            <IconButton
              v-if="showSettingsButton"
              :class="$style.settingsButton"
              @click="handleSettingsButtonClick"
            >
              <SettingsIcon :class="$style.settingsIcon" />
              Settings
            </IconButton>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="canExport || canModifyColumns || reportType"
      :class="$style.actionBarSecondary"
    >
      <RunReportButton
        v-if="canRunReport"
        :reportType="reportType"
      />
      <DropDownMultiSelectSearch
        v-if="canModifyColumns"
        :class="$style.columnsDropDown"
        :disabled="!totalCountOfAllItems"
        :displayValue="false"
        :maxOptionsSelectable="8"
        name="columns"
        :options="columnOptions"
        :sortOptions="false"
        :useDarkLinkStyleForButton="true"
        :value="columns.map(d => d.headingText)"
        @input="handleColumnsChange"
      >
        <template v-slot:footer>
          <div>
            <a
              :class="$style.revertColumnsLink"
              @click="revertToDefaultColumns"
            >
              Revert to default columns
            </a>
          </div>
        </template>
      </DropDownMultiSelectSearch>
      <ExportDropDown
        v-if="canExport"
        :disabled="totalCountOfAllItems === 0"
        :exportFunction="exportFunction"
        :itemText="selectionItemText.singular"
        @error="(props) => triggerExportSegmentEvent({ ...props, success: false })"
        @success="(props) => triggerExportSegmentEvent({ ...props, success: true })"
      />
    </div>
  </div>
</template>

<script>
import {
  ItemTypeConsts, ItemTypeDisplayNames, StoreModuleMap,
} from 'client/util/Constants';
import { MarkdownInline } from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { mixin as clickaway } from 'vue-clickaway';
import { extend, get } from 'lodash';
import { itemTypePages } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { menuTextColor } from 'client/css/colors.css';
import { notify } from 'client/util';
import DropDownList from 'client/components/DropDownMenu/DropDownList.vue';
import DropDownMenu from 'client/components/DropDownMenu/DropDownMenu.vue';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ExportDropDown from 'components/DropDownMenu/ExportDropDown.vue';
import IconButton from 'client/components/Buttons/IconButton.vue';
import ItemTableActionBarMessageList from 'client/components/ItemTable//ItemTableActionBarMessageList';
import ItemTableSearchBar from 'client/components/ItemTable/ItemTableSearchBar.vue';
import ReportMetadata from 'client/Reports/ReportMetadata';
import RunReportButton from 'client/Reports/components/RunReportButton';
import SettingsIcon from 'public/img/icons/nav/icon-nav-settings.svg?inline';
import SystemFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemUserFilter from 'client/systemusers/SystemUserItemTable/SystemUserFilter';
import Util from 'client/util/Util';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

export default {
  name: 'ItemTableActionBar',

  components: {
    RunReportButton,
    DropDownList,
    DropDownMenu,
    DropDownMultiSelectSearch,
    ExportDropDown,
    IconButton,
    ItemTableSearchBar,
    ItemTableActionBarMessageList,
    MarkdownInline,
    SettingsIcon,
    SystemUserFilter,
  },

  mixins: [
    clickaway,
  ],

  props: {
    additionalBulkActions: {
      type: Array,
      required: true,
    },
    boundItemsUrl: {
      type: String,
      required: true,
    },
    bulkActionText: {
      type: String,
      required: true,
    },
    bulkActionsList: {
      type: Array,
      default: () => [],
    },
    bulkDeleteText: {
      type: String,
      required: true,
    },
    canExport: {
      type: Boolean,
      default: false,
    },
    canModifyColumns: {
      type: Boolean,
      default: false,
    },
    columnOptions: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    // https://jumpcloud.atlassian.net/browse/CA-347
    disableBoundCount: {
      type: Boolean,
      default: false,
    },
    disableBulkAction: {
      type: Boolean,
      default: false,
    },
    disableSearchBar: {
      default: false,
      type: Boolean,
    },
    eventBus: {
      type: Object,
      required: true,
    },
    handleBulkAction: {
      type: Function,
      required: true,
    },
    handleBulkDelete: {
      type: Function,
      required: true,
    },
    handleMainAction: {
      type: Function,
      required: true,
    },
    handleSettingsButtonClick: {
      type: Function,
      default: () => {},
    },
    hideSelectionTotalCount: {
      type: Boolean,
      default: false,
    },
    inDetailsAsidePanel: {
      type: Boolean,
      required: true,
    },
    isAsideOpen: {
      type: Boolean,
      default: false,
    },
    isBulkMainActionDisabled: {
      type: Boolean,
      default: false,
    },
    isMainActionDisabled: {
      type: Boolean,
      default: false,
    },
    isRefreshButtonEnabled: {
      default: true,
      type: Boolean,
    },
    itemType: {
      type: String,
      default: '',
    },
    mainActionDisabledMessage: {
      type: String,
      default: '',
    },
    mainActionMenuItems: {
      type: Array,
      default: () => [],
    },
    mainActionMenuLabel: {
      type: String,
      default: '',
    },
    placeholderText: {
      type: String,
      default: '',
    },
    preventSearchReset: {
      default: false,
      type: Boolean,
    },
    reportType: {
      type: String,
      default: '',
    },
    selectionItemText: {
      type: Object,
      required: true,
    },
    setLoadingOverlay: {
      type: Function,
      required: true,
    },
    setSelectedModelId: {
      type: Function,
      required: true,
    },
    showBound: {
      type: Boolean,
      required: true,
    },
    showBulkAction: {
      type: Boolean,
      required: true,
    },
    showBulkDelete: {
      type: Boolean,
      required: true,
    },
    showMainAction: {
      type: Boolean,
      required: true,
    },
    showManagePolicyGroupTemplates: {
      type: Boolean,
      default: false,
    },
    showRefreshButton: {
      type: Boolean,
      default: false,
    },
    showSearchForm: {
      type: Boolean,
      default: false,
    },
    showSelectedItemsCount: {
      type: Boolean,
      default: true,
    },
    showSettingsButton: {
      type: Boolean,
      default: false,
    },
    storeModule: {
      type: String,
      required: true,
    },
    useDropDownFilterComponent: {
      type: String,
      default: '',
    },
    useModernSearchStyles: {
      type: Boolean,
      default: true,
    },
    useSearchContextFilterComponent: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      mainActionMenuVisible: false,
      systemUser: ItemTypeConsts.systemUser,
      filters: SystemFilters.secureTokenRemediationFilters(),
      report: ReportMetadata[this.reportType],
    };
  },

  computed: {
    ...mapState(settingsModule, ['orgId']),

    /**
     * The selected items count is used here so that when creating a new parent
     * object the count of bound items will reflect the number of selected
     * child items.
     */
    boundItemsLabelText: {
      get() {
        if (this.showSelectedItemsCount === false) {
          return `show ${this.$props.selectionItemText.label} ${this.selectionItemText.plural}`;
        }

        const selectedText = this.selectedItemsCount === 1
          ? this.selectionItemText.singular : this.selectionItemText.plural;
        return `show ${this.$props.selectionItemText.label} ${selectedText}\
          (${this.selectedItemsCount})`;
      },
    },

    boundItemsLabelTitle: {
      get() {
        return this.boundItemsTotalCount > this.maxNumBoundItems
          ? 'Disabled due to group size' : undefined;
      },
    },

    bulkActionsPositioningClass() {
      return !this.inDetailsAsidePanel
        ? this.$style.bulkActionsInMainPanel
        : this.$style.bulkActionsInAsidePanel;
    },

    canRunReport() {
      return this.reportType
        && this.$hasFeatureFlag(this.$featureFlags.synthesizedReports)
        && this.report.isEnabled(this);
    },

    exportFunction() {
      // The `CollectionModule.exportData` method takes 2 parameters: exportType, fileNamePrefix
      // Alternatively, the `ExportDropdown.exportFunction` prop expects one arg: exportType
      return exportType => this.exportData({
        exportType,
        fileNamePrefix: `jc${this.selectionItemText.singular}list`,
      });
    },

    hasMainActionMenu: {
      get() {
        return this.mainActionMenuItems.length > 0;
      },
    },

    isBoundItemsInputEnabled: {
      get() {
        // if checkbox is checked initially, let the user un-check it before disabling it
        if (this.usePaginationMethodForBoundDevices && this.areShowingBoundItems) return true;

        if (this.boundItemsUrl
            && this.boundItemsTotalCount > 0
            && this.boundItemsTotalCount <= this.maxNumBoundItems) {
          return true;
        }

        return false;
      },
    },

    isBulkEnabled: {
      get() {
        return (this.selectedItemsCount > 0) && !this.disableBulkAction;
      },
    },

    mainActionMenuSections() {
      const defaultComponentProps = {
        fill: menuTextColor,
        height: '18px',
        style: {
          left: '1px',
          marginRight: '10px',
          position: 'relative',
          verticalAlign: '-5%',
        },
        width: '18px',
      };

      const menuLabel = this.mainActionMenuLabel;
      const menuItems = this.mainActionMenuItems.map(menuItem => ({
        action: menuItem.clickHandler || menuItem.href,
        addSeparator: menuItem.addSeparator,
        component: menuItem.component,
        componentProps: menuItem.componentProps || defaultComponentProps,
        disabled: menuItem.disabled,
        external: menuItem.external,
        label: menuItem.text,
        showExternalLinkIcon: menuItem.showExternalLinkIcon || false,
        subLabel: menuItem.smallText,
      }));

      return [{ menuLabel, menuItems }];
    },

    mainActionTitle() {
      return this.isMainActionDisabled && this.mainActionDisabledMessage ? this.mainActionDisabledMessage : '';
    },

    selectionResultsText: {
      get() {
        if (this.hideSelectionTotalCount) return `**${this.selectedItemsCount}** ${this.$props.selectionItemText.label}`;
        if (this.selectedItemsCount === 0 || this.disableBoundCount) {
          if (this.totalCount === 1) {
            return `**1** ${this.$props.selectionItemText.singular}`;
          }
          return `**${this.totalCount}** ${this.$props.selectionItemText.plural}`;
        }
        return `**${this.selectedItemsCount}** of **${this.totalCountOfAllItems}** ${this.$props.selectionItemText.plural} ${this.$props.selectionItemText.label}`;
      },
    },

    selectionTextClass() {
      const classes = [];

      if (!this.inDetailsAsidePanel) {
        classes.push(this.$style.selectionTextInMainPanel);
      }
      if ((!this.showBulkAction
        && !this.showBulkDelete
        && !this.additionalBulkActions.length)
        || this.isAsideOpen) {
        classes.push(this.$style.selectionTextWithoutBulkActions);
      }
      return classes;
    },

    showSystemUserFilter() {
      return this.itemType === this.systemUser
        && !this.inDetailsAsidePanel;
    },

  },

  watch: {
    /**
     * When the bound items total count changes to zero and bound items are
     * being shown then toggle back to the show all items view since there are
     * no more bound items to view.
     */
    boundItemsTotalCount(newValue) {
      if (newValue === 0 && this.areShowingBoundItems) {
        this.setLoadingOverlay(true);

        this.showAllItems()
          .then(() => {
            this.setLoadingOverlay(false);
          })
          .catch(() => {
            this.setLoadingOverlay(false);
            notify(
              'error',
              `Unable to show all ${this.$props.selectionItemText.label}`,
            );
          });
      }
    },
  },

  async mounted() {
    this.eventBus.$on('mainActionButtonClick', () => {
      // Call the click handler in the next tick so that the vue-clickaway
      // directive doesn't immediately close the menu due to the current click
      // being _off_ the main action button.
      this.$nextTick(() => this.handleMainActionButtonClick());
    });
  },

  /**
   * Extend the computed properties and methods here, before the component is
   * created so that the namespace argument to mapActions() and mapGetters()
   * can be set dynamically from the props passed to the ItemTable component.
   * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
   */
  beforeCreate() {
    extend(
      this.$options.computed,
      mapGetters(this.$options.propsData.storeModule, {
        areShowingBoundItems: 'areShowingBoundItems',
        boundItemsTotalCount: 'getBoundItemsTotalCount',
        maxNumBoundItems: 'getMaxNumBoundItems',
        selectedItemsCount: 'getSelectedItemsCount',
        totalCount: 'getTotalCount',
        totalCountOfAllItems: 'getTotalCountOfAllItems',
      }),

      mapState(this.$options.propsData.storeModule, {
        usePaginationMethodForBoundDevices: state => state
          .features.usePaginationMethodForBoundDevices,
      }),
    );

    extend(
      this.$options.methods,
      mapActions(this.$options.propsData.storeModule, [
        'exportData',
        'fetchCollection',
        'refreshCollection',
        'showAllItems',
        'showBoundItems',
      ]),
    );
  },

  methods: {
    handleBoundItemsInputClick(checked) {
      this.setLoadingOverlay(true);

      if (checked) {
        this.showBoundItems()
          .then(() => {
            this.setLoadingOverlay(false);
          })
          .catch(() => {
            this.setLoadingOverlay(false);
            notify(
              'error',
              `Unable to show bound ${this.$props.selectionItemText.label}`,
            );
          });
      } else {
        this.showAllItems()
          .then(() => {
            this.setLoadingOverlay(false);
          })
          .catch(() => {
            this.setLoadingOverlay(false);
            notify(
              'error',
              `Unable to show all ${this.$props.selectionItemText.label}`,
            );
          });
      }
    },

    handleColumnsChange(columnNames) {
      this.$emit('columnsChange', columnNames);
    },

    handleMainActionButtonClick() {
      this.setSelectedModelId(null);

      if (this.hasMainActionMenu) {
        this.toggleMainActionMenu();
      }

      this.handleMainAction();

      UiEvents.triggerIconClicked({
        description: `Adds new ${ItemTypeDisplayNames[this.itemType]}`,
        page: itemTypePages[this.itemType] || this.$page,
        section: 'Item Table Action Bar',
        iconName: 'add',
        text: 'add',
      });
    },

    handleManagePolicyGroupTemplatesClick() {
      Util.redirect('/#/groups/configuration/managetemplates');
    },

    handleRefresh() {
      this.setLoadingOverlay(true);
      this.refreshCollection()
        .then(() => {
          this.setLoadingOverlay(false);
        })
        .catch((err) => {
          this.setLoadingOverlay(false);
          notify('error', get(err, 'response.data.message', 'There was an error refreshing the list!'));
        });
    },

    hideMainActionMenu() {
      this.mainActionMenuVisible = false;
    },

    revertToDefaultColumns() {
      this.$emit('revertColumns');
    },

    toggleMainActionMenu() {
      this.mainActionMenuVisible = !this.mainActionMenuVisible;
    },

    triggerExportSegmentEvent(props = {}) {
      EventHelper.triggerItemTableExported({
        type: this.selectionItemText.singular,
        ...props,
      });
    },
  },
};
</script>

<style scoped module>
@value (max-width-large) from '~css/breakpoints.css';
@value (
  jcGrey,
  jcWhite300,
  jcGrey200,
  jcGrey600,
  jcGrey900,
  jcSuccess
) from '~css/brand-colors.css';
@value (buttonShadow1, buttonShadow2) from '~css/shadows.css';
@value (
  buttonTransition,
  bulkActionButtonBackground,
  dropDownMenuTransition,
  panelSpeed,
  panelSpeedSm,
  panelTimingFunction) from '~css/transitions.css';

.actionBarMain {
  background-color: var(--jcWhite);
  color: jcGrey;
  display: flex;
  font-size: 11px;
  line-height: 13px;
  padding: 10px 0;
  position: relative;
  top: 0;
}

.actionBarSecondary {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 0 40px 16px;
}

.actionsContainer {
  align-items: center;
  display: flex;
  margin-left: 20px;
  min-width: 400px;
  width: 100%;
}

.actionsContent {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: -10px; /* offsets margin-top in searchBar and nonSearchActionsContainer */
  width: 100%;
}

@media (max-width: max-width-large) {
  .actionsContent { display: block; }
}

.announcement {
  cursor: default;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.3;
  position: absolute;
  right: 5rem;
  text-align: left;
  top: 5rem;
  width: 40ch !important;
}

.searchBar {
  flex: 1;
  margin-top: 10px;
  padding-right: 20px;
}

.nonSearchActionsContainer {
  align-items: center;
  display: flex;
  margin-top: 10px;
  padding-right: 20px;
  text-align: right;
  white-space: nowrap;
}

.mainActionButton {
  display: flex;
  margin-left: 20px;
}

.icon {
  fill: currentColor;
  font-size: 22px;
  height: 1em;
  margin: auto;
  transition: transform var(--jc-transition-duration) var(--jc-transition-function);
  width: 1em;
}

.mainActionButtonActive .icon {
  transform: rotate(45deg);
}

.mainActionButtonDisabled {
  background-color: jcGrey200;

  /* !important used to override: .btnMainAction (client/css) */
  color: jcGrey900 !important;
}

.mainActionMenu {
  left: 5px;
  max-width: 350px;
}

.mainActionMenuItem {
  list-style: none;
  padding: 6px 21px 6px 15px;
}

.mainActionMenuItem:first-child {
  padding-top: 12px;
}

.mainActionMenuItem:last-child {
  padding-bottom: 12px;
}

.mainActionMenuItemLink,
.mainActionMenuItemLink:visited,
.mainActionMenuItemLink:hover,
.mainActionMenuItemLink:focus {
  color: inherit;
  font-size: 12px;
  font-weight: 400;
  text-decoration: none;
}

.mainActionMenuItemIcon {
  font-size: 21px;
  margin-right: 8px;
  vertical-align: middle;
}

.selectionText {
  display: inline-block;
}

.selectionTextWithoutBulkActions {
  font-size: 1rem;
  font-weight: 700;
  padding-right: 13px;
}

.selectionResults {
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  min-width: 50px;
  text-transform: lowercase;
}

.selectionBoundItems {
  margin-left: 20px;
  text-transform: lowercase;
}

.selectionBoundItemsInput {
  margin-right: 3px !important;
}

.bulkActions { display: inline-block; }
.bulkButton { margin-left: 9px; }

button.settingsButton {
  align-items: center;
  color: var(--jcGrey800);
  display: flex;
  height: 18px;
  justify-content: space-between;
  margin: 0 10px;
  width: auto;
}

.manageTemplatesButton.manageTemplatesButton {
  align-items: center;
  color: var(--jcInformational);
  display: inline-flex;
  font-size: 14px;
  font-weight: bold;
  height: 20px;
  justify-content: space-between;
  margin: 0 20px;
  width: auto;
}

.manageTemplatesButton:hover {
  text-decoration: underline;
}

.settingsIcon {
  fill: var(--jcGrey800);
  height: 16px;
  margin-right: 4px;
}

.manageTemplateIcon {
  fill: var(--jcInformational);
  height: 12px;
  margin-bottom: -2px;
  margin-right: 4px;
}

.columnsDropDown {
  display: inline-block;
  font-weight: 400;
  margin-left: 16px;
  margin-right: 16px;
  vertical-align: middle;
}

.revertColumnsLink { cursor: pointer; }
</style>
